/**
 * Shared styles for the chip family — `Badge` (and every `BadgeWith*` variant)
 * and `Pill`.
 *
 * Chips of the same size have to read as the same object wherever they sit
 * side by side: an assignee badge and a stage pill in one column, a tag list,
 * a proposal diff. That only holds if they are built from ONE scale. Before
 * this module each component carried its own inline copy of the size maps,
 * which is how a hand-rolled stage pill ended up 26px against a 24px badge.
 *
 * Two tiers:
 *
 *  1. `CHIP_SIZE_CLASSES` — height and type. Identical for every text-bearing
 *     chip in both shapes, and every component composes from it, so height and
 *     font cannot drift between components.
 *  2. `CHIP_INSET_CLASSES` — horizontal padding and gap. These legitimately vary
 *     with what leads the chip (a round dot or image needs less room on its side
 *     than bare text does). The table holds the exact values the components
 *     shipped with, so centralizing them moved nothing; it is the one place to
 *     change if the family is ever normalized.
 *
 * Exported the same way `select.tsx` exports its trigger constants: so a
 * domain control that must render its own element — e.g. a react-aria Button
 * acting as a picker trigger — can still look exactly like the library chip.
 * Prefer `Badge`/`Pill` directly wherever a span will do.
 *
 * `__tests__/chip-sizing.test.tsx` pins every value here against an
 * independently written table.
 */

import type { BadgeTypes, Sizes } from './badge-types';

export type ChipShape = 'pill' | 'badge';

/** Outline + layout for each shape. Ring, not border: a ring costs no height. */
export const CHIP_SHAPE_CLASSES: Record<ChipShape, string> = {
  pill: 'size-max flex items-center whitespace-nowrap rounded-full ring-1 ring-inset',
  badge:
    'size-max flex items-center whitespace-nowrap rounded-md ring-1 ring-inset',
};

/** Height + type scale, shared by every text-bearing chip in both shapes. */
export const CHIP_SIZE_CLASSES: Record<Sizes, string> = {
  sm: 'py-0.5 text-xs font-medium',
  md: 'py-0.5 text-sm font-medium',
  lg: 'py-1 text-sm font-medium',
};

/**
 * What sits at the chip's edges, which decides its horizontal padding and gap.
 * - `text`              — label only
 * - `dot`               — leading status dot
 * - `iconLeading`       — leading icon
 * - `iconTrailing`      — trailing icon
 * - `image`             — leading round image (flag, avatar photo)
 * - `button`            — trailing remove button
 * - `buttonWithLeading` — leading content AND a trailing remove button
 */
export type ChipInset =
  | 'text'
  | 'dot'
  | 'iconLeading'
  | 'iconTrailing'
  | 'image'
  | 'button'
  | 'buttonWithLeading';

/**
 * Horizontal padding + gap per shape × size × inset. The square badge steps its
 * radius up to `rounded-lg` at `lg`; that lives here because it is coupled to
 * size (tailwind-merge resolves it over the shape's `rounded-md`).
 */
export const CHIP_INSET_CLASSES: Record<
  ChipShape,
  Record<Sizes, Record<ChipInset, string>>
> = {
  pill: {
    sm: {
      text: 'px-2',
      dot: 'gap-1 pl-1.5 pr-2',
      iconLeading: 'gap-0.5 pl-1.5 pr-2',
      iconTrailing: 'gap-0.5 pl-2 pr-1.5',
      image: 'gap-1 pl-0.75 pr-2',
      button: 'gap-0.5 pl-2 pr-0.75',
      buttonWithLeading: 'gap-1 pl-0.75 pr-0.75',
    },
    md: {
      text: 'px-2.5',
      dot: 'gap-1.5 pl-2 pr-2.5',
      iconLeading: 'gap-1 pl-2 pr-2.5',
      iconTrailing: 'gap-1 pl-2.5 pr-2',
      image: 'gap-1.5 pl-1 pr-2.5',
      button: 'gap-0.5 pl-2.5 pr-1',
      buttonWithLeading: 'gap-1 pl-1 pr-1',
    },
    lg: {
      text: 'px-3',
      dot: 'gap-1.5 pl-2.5 pr-3',
      iconLeading: 'gap-1 pl-2.5 pr-3',
      iconTrailing: 'gap-1 pl-3 pr-2.5',
      image: 'gap-1.5 pl-1.5 pr-3',
      button: 'gap-0.5 pl-3 pr-1.5',
      buttonWithLeading: 'gap-1 pl-1.5 pr-1.5',
    },
  },
  badge: {
    sm: {
      text: 'px-1.5',
      dot: 'gap-1 px-1.5',
      iconLeading: 'gap-0.5 pl-1.5 pr-2',
      iconTrailing: 'gap-0.5 pl-2 pr-1.5',
      image: 'gap-1 pl-1 pr-1.5',
      button: 'gap-0.5 pl-1.5 pr-0.75',
      buttonWithLeading: 'gap-1 pl-0.75 pr-0.75',
    },
    md: {
      text: 'px-2',
      dot: 'gap-1.5 px-2',
      iconLeading: 'gap-1 pl-1.5 pr-2',
      iconTrailing: 'gap-1 pl-2 pr-1.5',
      image: 'gap-1.5 pl-1.5 pr-2',
      button: 'gap-0.5 pl-2 pr-1',
      buttonWithLeading: 'gap-1 pl-1 pr-1',
    },
    lg: {
      text: 'px-2.5 rounded-lg',
      dot: 'gap-1.5 px-2.5 rounded-lg',
      iconLeading: 'gap-1 pl-2 pr-2.5 rounded-lg',
      iconTrailing: 'gap-1 pl-2.5 pr-2 rounded-lg',
      image: 'gap-1.5 pl-2 pr-2.5 rounded-lg',
      button: 'gap-0.5 pl-2.5 pr-1.5 rounded-lg',
      buttonWithLeading: 'gap-1 pl-1.5 pr-1.5 rounded-lg',
    },
  },
};

/** Icon-only chips carry no text, so padding alone sets their size. */
export const CHIP_ICON_ONLY_CLASSES: Record<
  ChipShape,
  Record<Sizes, string>
> = {
  pill: { sm: 'p-1.25', md: 'p-1.5', lg: 'p-2' },
  badge: { sm: 'p-1.25', md: 'p-1.5', lg: 'p-2 rounded-lg' },
};

/** Affordance for an activatable chip (see `useActivatable`). */
// Canonical focus recipe (VISUAL_STYLE_GUIDE.md § Focus rings) — the previous
// `outline-none` + hardcoded brand ring went gray-on-gray in dark mode.
export const CHIP_INTERACTIVE_CLASSES =
  'cursor-pointer transition-colors outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2';

/** An activatable chip that is currently blocked. */
export const CHIP_DISABLED_CLASSES = 'cursor-not-allowed opacity-50';

/** `pill-color` badges are pills; `color` and `modern` badges are square. */
export function chipShapeForBadgeType(type: BadgeTypes): ChipShape {
  return type === 'pill-color' ? 'pill' : 'badge';
}
